import PageHero from "../components/PageHero";
import SmartLink from "../components/SmartLink";
import { ArrowRightIcon } from "../components/Icons";
import { apiReference } from "../lib/apiReference";

/** Colour-coded HTTP method chip. */
function Method({ method }: { method: string }) {
  const tone =
    method === "GET"
      ? "bg-brand-50 text-brand-700"
      : method === "POST"
        ? "bg-accent-success/15 text-accent-success"
        : "bg-accent-danger/10 text-accent-danger";

  return (
    <span className={`rounded-md px-2 py-1 text-[11px] font-bold tracking-wide ${tone}`}>
      {method}
    </span>
  );
}

function CodeBlock({ code }: { code: string }) {
  return (
    <pre className="mt-4 overflow-x-auto rounded-xl border border-ink-line bg-ink-soft p-4 text-xs leading-relaxed text-ink">
      <code>{code}</code>
    </pre>
  );
}

export default function ApiReferencePage() {
  return (
    <>
      <PageHero
        eyebrow={apiReference.eyebrow}
        title={apiReference.title}
        intro={apiReference.intro}
        actions={
          <>
            <SmartLink href={apiReference.primaryCta.href} className="btn-primary">
              {apiReference.primaryCta.label}
              <ArrowRightIcon />
            </SmartLink>
            <SmartLink href={apiReference.secondaryCta.href} className="btn-ghost">
              {apiReference.secondaryCta.label}
            </SmartLink>
          </>
        }
      />

      {/* Authentication */}
      <section className="section-pad bg-white">
        <div className="container-page">
          <div className="max-w-3xl">
            <h2 className="h-section">{apiReference.authHeading}</h2>
            <p className="lead">{apiReference.authBody}</p>

            <ul className="mt-6 flex flex-wrap gap-2">
              {apiReference.authTags.map((tag) => (
                <li
                  key={tag}
                  className="rounded-full border border-ink-line bg-ink-soft px-3 py-1 text-xs font-medium text-ink-muted"
                >
                  {tag}
                </li>
              ))}
            </ul>

            <CodeBlock code={apiReference.authSample} />

            <dl className="mt-8 rounded-2xl border border-ink-line p-5">
              <dt className="text-[11px] font-semibold uppercase tracking-[0.16em] text-ink-muted">
                {apiReference.baseUrlLabel}
              </dt>
              <dd className="mt-1 font-mono text-sm font-semibold text-ink">
                {apiReference.baseUrl}
              </dd>
              <dd className="mt-2 text-xs text-ink-muted">{apiReference.baseUrlNote}</dd>
            </dl>
          </div>
        </div>
      </section>

      {/* Endpoints */}
      <section className="section-pad bg-ink-soft">
        <div className="container-page">
          <h2 className="h-section">{apiReference.endpointsHeading}</h2>

          <div className="mt-10 space-y-6">
            {apiReference.endpoints.map((endpoint) => (
              <div key={`${endpoint.method}-${endpoint.path}`} className="card p-6 shadow-card">
                <div className="flex flex-wrap items-center gap-3">
                  <Method method={endpoint.method} />
                  <code className="font-mono text-sm font-semibold text-ink">
                    {endpoint.path}
                  </code>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-ink-muted">
                  {endpoint.description}
                </p>
                <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.16em] text-ink-muted">
                  Example response
                </p>
                <CodeBlock code={endpoint.sample} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Rate limits & codes */}
      <section className="section-pad bg-white">
        <div className="container-page">
          <h2 className="h-section">{apiReference.limitsHeading}</h2>

          <div className="mt-10 grid gap-8 lg:grid-cols-2">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-ink-muted">
                Rate limits
              </h3>
              <table className="mt-4 w-full border-collapse text-sm">
                <tbody>
                  {apiReference.rateLimits.map((row) => (
                    <tr key={row.plan} className="border-b border-ink-line">
                      <th scope="row" className="py-3 text-left font-semibold text-ink">
                        {row.plan}
                      </th>
                      <td className="py-3 text-right text-ink-muted">{row.limit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div>
              <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-ink-muted">
                HTTP status codes
              </h3>
              <table className="mt-4 w-full border-collapse text-sm">
                <tbody>
                  {apiReference.statusCodes.map((row) => (
                    <tr key={row.code} className="border-b border-ink-line">
                      <th
                        scope="row"
                        className="w-16 py-3 text-left font-mono font-semibold text-ink"
                      >
                        {row.code}
                      </th>
                      <td className="py-3 text-ink-muted">{row.meaning}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Custom integration */}
      <section className="section-pad bg-ink-soft">
        <div className="container-page">
          <div className="card mx-auto max-w-3xl p-8 text-center shadow-card sm:p-12">
            <h2 className="text-2xl font-bold tracking-tight text-ink sm:text-3xl">
              {apiReference.customHeading}
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-ink-muted">
              {apiReference.customBody}
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <SmartLink href={apiReference.customCta.href} className="btn-primary w-full sm:w-auto">
                {apiReference.customCta.label}
              </SmartLink>
              <SmartLink
                href={apiReference.customSecondaryCta.href}
                className="btn-ghost w-full sm:w-auto"
              >
                {apiReference.customSecondaryCta.label}
              </SmartLink>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
